/**
 * « Lignes à relire » (ADR-013, decision 6): the rows the engine did not read, sorted without any classifier,
 * by what downstream in the piece can tell of them (schema 1.6.0, `rows[].per_size[].oracle` and `gap`):
 *
 * - `gap`: a stated count follows before any other unread line, and it says the line changes the count
 *   (`oracle: exact`, `gap` ≠ 0): the line surely changes the count, by that much;
 * - `exact`: a stated count follows, and says nothing changes (`gap` 0) or cannot say by how much (`gap` null);
 * - `weak`: no stated count follows, but a later explicit row does not work out on the carried count;
 * - `none`: nothing further on can tell: the notebook says to add a stated count after the line;
 * - `unknown`: the result does not say (an engine older than 1.6.0, or one that leaves the field empty).
 *
 * Nothing here guesses: the order and the sentences come from the engine's fields alone. The proposals of each
 * line (`rows[].proposals`, by rank) are shown, never applied.
 */
import type { CompileResult, Proposal, Row, RowSizeResult } from '../engine/types';
import { formatNumber, type Lang, type Translate } from '../i18n';

export type ReviewCategory = 'gap' | 'exact' | 'weak' | 'none' | 'unknown';

/** The order of the section: the line that surely changes the count first, the line nothing can check last. */
export const REVIEW_ORDER: readonly ReviewCategory[] = ['gap', 'exact', 'weak', 'none', 'unknown'];

const RANK: Record<ReviewCategory, number> = { gap: 0, exact: 1, weak: 2, none: 3, unknown: 4 };

export interface ReviewSize {
  size: number;
  category: ReviewCategory;
  /** Count carried over the line (stitches before it), when known. */
  available: number | null;
  /** Change of count the pattern needs from the line (`oracle: exact` only), when known. */
  gap: number | null;
}

export interface ReviewEntry {
  row: Row;
  lineNo: number;
  /** The line as written (null when the result does not carry it). */
  text: string | null;
  /** The best category over the sizes considered: what the sentence says. */
  category: ReviewCategory;
  /** The sizes in that category, in the pattern's order. */
  sizes: ReviewSize[];
  /** Every size considered where the line is not read. */
  unreadSizes: number[];
  /** The readings found and not applied, by rank. */
  proposals: Proposal[];
}

export function sizeCategory(ps: Pick<RowSizeResult, 'oracle' | 'gap'>): ReviewCategory {
  switch (ps.oracle) {
    case 'exact':
      return typeof ps.gap === 'number' && ps.gap !== 0 ? 'gap' : 'exact';
    case 'weak':
      return 'weak';
    case 'none':
      return 'none';
    default:
      return 'unknown';
  }
}

const num = (v: number | null | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The lines to review for the selected size (or every size): the rows not read (`status: unread`) where they apply,
 * sorted by category, then by line. A row not read in several sizes takes its best category among them.
 */
export function reviewEntries(result: CompileResult, selectedSize: number | null): ReviewEntry[] {
  const texts = new Map(result.lines.map((l) => [l.line_no, l.text]));
  const entries: ReviewEntry[] = [];
  for (const row of result.rows) {
    const unread = row.per_size.filter((ps) => ps.applies && ps.status === 'unread' && (selectedSize === null || ps.size === selectedSize));
    if (unread.length === 0) continue;
    const sizes: ReviewSize[] = unread.map((ps) => ({ size: ps.size, category: sizeCategory(ps), available: num(ps.available), gap: num(ps.gap) }));
    const category = sizes.reduce<ReviewCategory>((best, s) => (RANK[s.category] < RANK[best] ? s.category : best), 'unknown');
    entries.push({
      row,
      lineNo: row.line_no,
      text: texts.get(row.line_no) ?? null,
      category,
      sizes: sizes.filter((s) => s.category === category).sort((a, b) => a.size - b.size),
      unreadSizes: unread.map((ps) => ps.size).sort((a, b) => a - b),
      proposals: [...(row.proposals ?? [])].sort((a, b) => a.rank - b.rank),
    });
  }
  return entries.sort((a, b) => RANK[a.category] - RANK[b.category] || a.lineNo - b.lineNo);
}

/** « 70 (77, 84) »: one value per size, written the way a pattern writes them (the first, the others in brackets). */
export function patternValues(values: readonly string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  return `${values[0]} (${values.slice(1).join(', ')})`;
}

/** « −10 », « +8 », « 0 »: a change of count, signed (a true minus sign). */
export function signed(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return '0';
}

/** Why the line sits where it does, in one sentence, from the engine's `oracle` and `gap` alone. */
export function reviewReason(entry: ReviewEntry, t: Translate, lang: Lang): string {
  const fmt = (n: number) => formatNumber(lang, n);
  switch (entry.category) {
    case 'gap': {
      if (entry.sizes.every((s) => s.available !== null && s.gap !== null)) {
        return t('review.reason.gapFromTo', {
          from: patternValues(entry.sizes.map((s) => fmt(s.available!))),
          to: patternValues(entry.sizes.map((s) => fmt(s.available! + s.gap!))),
        });
      }
      return t('review.reason.gapBy', { gap: patternValues(entry.sizes.map((s) => (s.gap === null ? '?' : signed(s.gap)))) });
    }
    case 'exact':
      return t(entry.sizes.every((s) => s.gap === 0) ? 'review.reason.exactZero' : 'review.reason.exact');
    case 'weak':
      return t('review.reason.weak');
    case 'none':
      return t('review.reason.none');
    default:
      return t('review.reason.unknown');
  }
}

/** The canonical form split around its holes (« ? »), so each hole can be shown apart. */
export function canonicalParts(canonical: string): Array<{ text: string; hole: boolean }> {
  const parts: Array<{ text: string; hole: boolean }> = [];
  for (const piece of canonical.split(/(\?)/)) {
    if (piece === '') continue;
    parts.push({ text: piece, hole: piece === '?' });
  }
  return parts;
}

/** Slots a stated count filled (never applied: the designer decides). */
export function oracleSlots(p: Proposal): string[] {
  return Object.entries(p.filled)
    .filter(([, how]) => how === 'oracle')
    .map(([slot]) => slot);
}
